import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [selectedStudy, setSelectedStudy] = useState(() => {
    const stored = localStorage.getItem('selectedStudy');
    return stored ? JSON.parse(stored) : null;
  });
  // Role impersonation for System Admin
  const [impersonatedRole, setImpersonatedRole] = useState(() => {
    const stored = localStorage.getItem('impersonatedRole');
    return stored ? JSON.parse(stored) : null;
  });

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error('Auth init failed:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('selectedStudy');
          setToken(null);
          setUser(null);
          setSelectedStudy(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password
    });
    
    // Check if MFA is required
    if (response.data.mfa_required) {
      // Return MFA data for the login page to handle
      return {
        mfa_required: true,
        mfa_token: response.data.mfa_token,
        mfa_method: response.data.mfa_method,
        email_hint: response.data.email_hint,
        otp_for_testing: response.data.otp_for_testing
      };
    }
    
    // No MFA - proceed with login (clear any previous study selection)
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.removeItem('selectedStudy');
    setToken(access_token);
    setUser(userData);
    setSelectedStudy(null);
    return { mfa_required: false, user: userData };
  };

  // Complete login after MFA verification
  const completeMfaLogin = (data) => {
    const { access_token, user: userData } = data;
    localStorage.setItem('token', access_token);
    localStorage.removeItem('selectedStudy');
    setToken(access_token);
    setUser(userData);
    setSelectedStudy(null);
    return userData;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, {
      name,
      email,
      password
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('selectedStudy');
    localStorage.removeItem('impersonatedRole');
    setToken(null);
    setUser(null);
    setSelectedStudy(null);
    setImpersonatedRole(null);
  };

  // Role impersonation functions for System Admin
  const startImpersonation = (role) => {
    if (!isAdmin()) return false;
    const impersonation = {
      role,
      originalRole: user?.role,
      startedAt: new Date().toISOString()
    };
    localStorage.setItem('impersonatedRole', JSON.stringify(impersonation));
    setImpersonatedRole(impersonation);
    return true;
  };

  const stopImpersonation = () => {
    localStorage.removeItem('impersonatedRole');
    setImpersonatedRole(null);
  };

  const isImpersonating = () => !!impersonatedRole;

  // Get effective role (considering impersonation)
  const getEffectiveRole = () => {
    if (impersonatedRole?.role) {
      return impersonatedRole.role;
    }
    return user?.role;
  };

  // Update selected study and persist to localStorage
  const updateSelectedStudy = (study) => {
    if (study) {
      localStorage.setItem('selectedStudy', JSON.stringify(study));
    } else {
      localStorage.removeItem('selectedStudy');
    }
    setSelectedStudy(study);
  };

  // Clear study selection (go back to study select page)
  const clearStudySelection = () => {
    localStorage.removeItem('selectedStudy');
    setSelectedStudy(null);
  };

  // Get current role - use study-specific role if a study is selected, otherwise user's base role
  const getCurrentRole = () => {
    if (selectedStudy?.role) {
      return selectedStudy.role;
    }
    return user?.role;
  };

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  const isAdmin = () => ['admin', 'system_admin'].includes(user?.role);
  
  // Check if user can upload documents based on role or permissions
  const isEditor = () => {
    // Legacy roles
    if (['admin', 'editor', 'system_admin'].includes(user?.role)) return true;
    
    // New clinical roles with upload permission
    const uploaderRoles = [
      'tmf_lead',    // TMF Lead / Manager
      'ctm',         // Clinical Trial Manager
      'cra',         // CRA / Monitor
      'cdm',         // Clinical Data Manager
      'regulatory',  // Regulatory Affairs
      'site_user',   // Site User (PI / Coordinator)
      'vendor',      // CRO / Vendor User
    ];
    if (uploaderRoles.includes(user?.role)) return true;
    
    // Check explicit permission
    return user?.permissions?.can_upload === true;
  };
  
  // Check if user can approve/QC documents
  const canApprove = () => {
    const approverRoles = ['tmf_lead', 'qc_reviewer', 'regulatory'];
    if (approverRoles.includes(user?.role)) return true;
    return user?.permissions?.can_approve === true || user?.permissions?.can_qc_review === true;
  };
  
  // Check if user can perform QC review
  const canQCReview = () => {
    const qcRoles = ['tmf_lead', 'qc_reviewer', 'regulatory'];
    if (qcRoles.includes(user?.role)) return true;
    return user?.permissions?.can_qc_review === true;
  };
  
  // Check if user can export documents
  const canExport = () => {
    const exporterRoles = ['tmf_lead', 'ctm', 'cdm', 'qa_auditor', 'regulatory', 'inspector'];
    if (exporterRoles.includes(user?.role)) return true;
    return user?.permissions?.can_export === true;
  };
  
  // Check if user can delete documents (very restricted - no role has this by default per RBAC matrix)
  const canDelete = () => {
    // Per the RBAC matrix, NO role has delete permission by default (segregation of duties)
    // Only check explicit permission
    return user?.permissions?.can_delete === true;
  };
  
  // Check if user can edit document metadata (restricted to QC, QA, and TMF Lead only for OTHER users' documents)
  const canEditMetadata = () => {
    // QC Reviewer, QA/Auditor, and TMF Lead can edit ANY document's metadata
    const metadataEditRoles = ['tmf_lead', 'qc_reviewer', 'qa_auditor'];
    if (metadataEditRoles.includes(user?.role)) return true;
    return user?.permissions?.can_edit_metadata === true;
  };
  
  // Check if user has limited edit (only own documents)
  const hasLimitedEdit = () => {
    // All roles except inspector can edit their OWN documents
    const noEditRoles = ['inspector'];
    if (noEditRoles.includes(user?.role)) return false;
    // Other roles can edit their own uploads
    return user?.permissions?.can_edit_metadata_limited === true || 
           !['tmf_lead', 'qc_reviewer', 'qa_auditor'].includes(user?.role);
  };
  
  // Check if user can edit a specific document (considers document ownership)
  const canEditDocument = (document) => {
    // QC/QA/TMF Lead can edit any document
    if (canEditMetadata()) return true;
    // Others can only edit their own documents
    if (hasLimitedEdit() && document?.uploaded_by === user?.id) return true;
    return false;
  };
  
  // Check if user can manage workflows
  const canManageWorkflow = () => {
    const workflowRoles = ['system_admin', 'tmf_lead', 'ctm', 'cdm', 'regulatory'];
    if (workflowRoles.includes(user?.role)) return true;
    return user?.permissions?.can_manage_workflow === true;
  };
  
  // Check if user can sign documents (e-signatures)
  const canSign = () => {
    const signerRoles = ['tmf_lead', 'qc_reviewer', 'regulatory'];
    if (signerRoles.includes(user?.role)) return true;
    return user?.permissions?.can_sign === true;
  };
  
  // Check if user can download documents (everyone can view/download, but exports are restricted)
  const canDownload = () => {
    // All roles can view and download individual documents
    // This is different from bulk export which is restricted
    return user?.permissions?.can_view !== false; // Default to true unless explicitly disabled
  };
  
  // Check if user is in read-only inspection mode
  const isInspector = () => {
    if (user?.role === 'inspector') return true;
    return user?.permissions?.is_inspection_mode === true;
  };
  
  // Check if user can view audit logs
  const canViewAuditLogs = () => {
    const auditRoles = ['system_admin', 'tmf_lead', 'qa_auditor', 'regulatory', 'inspector'];
    if (auditRoles.includes(user?.role)) return true;
    return user?.permissions?.can_view_audit_logs === true;
  };
  
  // Check if user can create studies
  const canCreateStudy = () => {
    const studyRoles = ['system_admin', 'tmf_lead', 'ctm'];
    if (studyRoles.includes(user?.role)) return true;
    return user?.permissions?.can_create_study === true;
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    completeMfaLogin,
    getAuthHeaders,
    isAdmin,
    isEditor,
    canApprove,
    canQCReview,
    canExport,
    canDelete,
    canEditMetadata,
    hasLimitedEdit,
    canEditDocument,
    canManageWorkflow,
    canSign,
    canDownload,
    isInspector,
    canViewAuditLogs,
    canCreateStudy,
    isAuthenticated: !!user,
    // Study context
    selectedStudy,
    setSelectedStudy: updateSelectedStudy,
    clearStudySelection,
    getCurrentRole,
    hasStudySelected: !!selectedStudy,
    // Role impersonation
    impersonatedRole,
    startImpersonation,
    stopImpersonation,
    isImpersonating,
    getEffectiveRole
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
